import { animate, state, style, transition, trigger } from '@angular/animations';
import { CommonModule } from '@angular/common';
import {
    Component,
    DestroyRef,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
    inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { SplitButtonComponent } from '@app/components/platform';
import { AnalyticEvents } from '@app/enums';
import { ProjectAssignment } from '@app/models/time-tracking/project-assignment.model';
import { TimeClockEntry } from '@app/models/time-tracking/time-clock-entry.model';
import { PayrollHourType, Settings } from '@app/modules/time-tracking/interfaces/settings.interface';
import { INCOME_TYPES } from '@app/modules/time-tracking/meta/time-tracking-payroll-income-types';
import { TimeTrackingSettingsService } from '@app/modules/time-tracking/services/time-tracking-settings.service';
import { AppDatePipe } from '@app/pipes';
import { PlatformModule } from '@app/platform.module';
import { AnalyticService, AuthService, NotifyService } from '@app/services';
import { TranslateModule } from '@ngx-translate/core';
import moment, { Moment } from 'moment';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { TimeClockService } from '../../services/time-clock.service';
import { EmployeeHourTypesService } from './../../services/employee-hour-types.service';

@Component({
    selector: 'app-time-clock-widget',
    templateUrl: './time-clock.component.html',
    styleUrls: ['./time-clock.component.scss'],
    standalone: true,
    imports: [CommonModule, TranslateModule, PlatformModule, FormsModule, SplitButtonComponent],
    animations: [
        trigger('activeState', [
            state(
                'inactive',
                style({
                    backgroundColor: 'var(--time-clock-inactive-bg)',
                })
            ),
            state(
                'active',
                style({
                    backgroundColor: 'var(--time-clock-active-bg)',
                })
            ),
            transition('inactive => active', [
                animate('300ms ease-in-out', style({ backgroundColor: 'var(--time-clock-active-bg)' })),
            ]),
            transition('active => inactive', [
                animate('300ms ease-in-out', style({ backgroundColor: 'var(--time-clock-inactive-bg)' })),
            ]),
        ]),
        trigger('textActiveState', [
            state(
                'inactive',
                style({
                    color: '*',
                })
            ),
            state(
                'active',
                style({
                    color: 'var(--primary-600)',
                })
            ),
            transition('inactive => active', [animate('1s ease-in-out', style({ color: 'var(--primary-500)' }))]),
            transition('active => inactive', [animate('300ms ease-in-out', style({ color: '*' }))]),
        ]),
        trigger('fadeInOut', [
            state(
                'void',
                style({
                    opacity: 0,
                })
            ),
            transition('void <=> *', animate('300ms ease-in-out')),
        ]),
    ],
})
export class TimeClockComponent implements OnInit, OnDestroy, OnChanges {
    @Input() minutesToday = 0;
    @Input() startingTime: string | null = null;
    @Input() timeClockEntries: TimeClockEntry[] = [];
    @Output() onClockOut: EventEmitter<void> = new EventEmitter<void>();
    /**
     * Ensures that the buttons are the same width and always the width of the largest button.
     * This ensures a dynamic solution that works even when copy changes or language
     */
    @ViewChild('buttonsBox') set buttonsBox(buttonsBox: ElementRef) {
        // When the containing div loads into the DOM we calculate the largest button width of the two children
        this.buttonMaxWidth =
            Math.max(
                ...Array.from((<HTMLElement>buttonsBox.nativeElement).querySelectorAll('ui-button')).map(
                    ({ clientWidth }) => clientWidth
                )
            ) + 'px';
    }
    buttonMaxWidth = 'max-content';

    today = new Date();
    dayOfTheWeek: string;
    dayOfMonth: number;
    abbreviatedMonth: string;

    timeWorkedToday = '';
    timeWorkedYesterday = '';
    projectAssignments: ProjectAssignment[] = [];
    selectedProjectId: number | null = null;
    isClockedIn = false;
    activeTimer: string = '00:00:00';
    timeClockEntry = new TimeClockEntry({ employeeId: this.auth.employee.id });
    isProcessing = false;
    latestActiveEntry: TimeClockEntry | null = null;
    isLoading = false;
    incomeTypes = INCOME_TYPES;

    hourTypes: PayrollHourType[] = [];
    selectedHourType: PayrollHourType | null = null;
    timeTrackingSettings: Settings | null = null;

    private timerInterval: NodeJS.Timeout | null = null;
    private timerStartMoment: Moment | null = null;
    private clockClickSubject = new Subject<void>();

    private destroyRef = inject(DestroyRef);

    constructor(
        private auth: AuthService,
        private notify: NotifyService,
        private timeClockService: TimeClockService,
        private employeeHourTypesService: EmployeeHourTypesService,
        private timeTrackingSettingsService: TimeTrackingSettingsService,
        private analyticService: AnalyticService
    ) {
        // set the display values for today's date
        // these values will not change
        const datePipe = new AppDatePipe();
        const now = new Date();
        this.dayOfMonth = now.getDate();
        this.dayOfTheWeek = datePipe.transform(now, 'EEEE') ?? '';
        this.abbreviatedMonth = datePipe.transform(now, 'LLL') ?? '';

        this.clockClickSubject.pipe(debounceTime(300), takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            this.toggleTimeClockEntry();
        });
    }

    async ngOnInit(): Promise<void> {
        this.isLoading = true;
        this.timeTrackingSettings = await this.timeTrackingSettingsService.get();
        await this.auth.employee.loadSalaries();
        await this.loadProjects();
        await this.loadLatestActiveEntry();
        await this.setHourTypes();
    }

    async loadProjects(): Promise<void> {
        const [projectAssignments] = await ProjectAssignment.with(['project', 'employee'])
            .where('employeeId', this.auth.employee.id)
            .all();

        this.projectAssignments = projectAssignments;
    }

    ngOnChanges(): void {
        this.calculateHoursAndMinutes();
    }

    ngOnDestroy(): void {
        this.stopTimer();
    }

    async toggleTimeClockEntry(hourType?: PayrollHourType | null): Promise<void> {
        if (this.isProcessing) return;

        if (await this.checkForOverlappingEntries()) {
            return;
        }

        // Only set default hour type if no explicit selection was made (undefined)
        if (hourType === undefined && !this.isClockedIn) {
            if (this.isSalariedEmployee()) {
                hourType = null; // Set to unpaid for salaried employees
            } else if (this.hourTypes.length > 0) {
                // For hourly employees, try to find Regular hours first
                hourType = this.hourTypes.find((type) => type.name === this.incomeTypes.Regular) || this.hourTypes[0];
            }
        }

        this.isProcessing = true;
        try {
            if (this.isClockedIn) {
                this.updateTimeClockBasedOnHourType(hourType ? hourType : this.selectedHourType);
                await this.clockOut();
                this.onClockOut.emit();
            } else {
                this.updateTimeClockBasedOnHourType(hourType ?? null);
                await this.clockIn();
            }

            this.isClockedIn = !this.isClockedIn;
        } catch (error) {
            this.notify.error(error);
        } finally {
            this.isProcessing = false;
        }
    }

    /**
     * updateTimeClockBasedOnHourType responds to an hour type being selected by updating
     * the required attributes on the time clock entry. This is because "hour type" is not an actual
     * attribute of time clock entries.
     */
    updateTimeClockBasedOnHourType(payrollHourType: PayrollHourType | null): void {
        this.timeClockEntry.salaryId = null;
        this.timeClockEntry.incomeType = null;
        this.timeClockEntry.incomeTypeId = null;

        // If no hour type selected or employee is salaried (no hour types)
        if (!payrollHourType || !this.hourTypes.length) {
            this.selectedHourType = null;
            return;
        }

        if (payrollHourType.salaryId) {
            this.timeClockEntry.salaryId = payrollHourType.salaryId;
        } else {
            this.timeClockEntry.incomeType = payrollHourType.name;
            this.timeClockEntry.incomeTypeId = payrollHourType.id;
        }

        this.selectedHourType = payrollHourType;
    }

    async onSaveClockIn(): Promise<void> {
        if (this.timerStartMoment) {
            this.timeClockEntry.startTime = this.timerStartMoment.format('YYYY-MM-DD HH:mm:ss');
            this.timeClockEntry.date = this.timerStartMoment.format('YYYY-MM-DD');
            this.timeClockEntry.projectId = this.selectedProjectId;
            const savedEntry = await this.timeClockEntry.save();
            this.latestActiveEntry = savedEntry;
        }
    }

    async onSaveClockOut(): Promise<void> {
        if (this.timerStartMoment && this.latestActiveEntry) {
            const endMoment = moment();
            const duration = moment.duration(endMoment.diff(this.timerStartMoment));

            let roundedEndMoment: Moment;
            if (duration.asMinutes() < 1) {
                // If duration is less than a minute, set end time to one minute after start time
                roundedEndMoment = moment(this.timerStartMoment).add(1, 'minute');
            } else {
                // Otherwise, round to the nearest minute
                const roundedMinutes = Math.round(duration.asMinutes());
                roundedEndMoment = moment(this.timerStartMoment).add(roundedMinutes, 'minutes');
            }

            this.latestActiveEntry.endTime = roundedEndMoment.format('YYYY-MM-DD HH:mm:ss');
            this.latestActiveEntry.projectId = this.selectedProjectId;
            // Reset the timer
            this.stopTimer();

            try {
                const savedEntry = await this.latestActiveEntry.save();
                this.selectedHourType = null;

                this.notify.success('time-tracking.timeClockWidget.hoursSaved', {
                    onClick: ['/time-tracking', 'my-timesheet', savedEntry.timeTrackingTimesheetEntryId, 'edit'],
                    text: 'time-tracking.timeClockWidget.viewTimesheet',
                });

                this.latestActiveEntry = null;
                this.timeClockEntry = new TimeClockEntry({ employeeId: this.auth.employee.id });
            } catch (error) {
                this.notify.error(error);
            }
        }
    }

    getActiveState(): string {
        return this.isClockedIn ? 'active' : 'inactive';
    }

    private isSalariedEmployee(): boolean {
        return this.auth.employee.salaries.some((salary) => salary.frequency === 'year' && salary.current);
    }

    private async clockIn(): Promise<void> {
        this.startTimer();
        await this.onSaveClockIn();
    }

    private async clockOut(): Promise<void> {
        await this.onSaveClockOut();
    }

    private startTimer(startTime?: Moment): void {
        this.timerStartMoment = startTime || moment();
        this.updateActiveTimer(); // Update the timer immediately
        this.timerInterval = setInterval(() => {
            this.updateActiveTimer();
        }, 1000);
    }

    private stopTimer(): void {
        if (this.timerInterval) {
            clearInterval(this.timerInterval);
        }
        this.activeTimer = '00:00:00';
        this.timerStartMoment = null;
    }

    private updateActiveTimer(): void {
        if (!this.timerStartMoment) return;

        const duration = moment.duration(moment().diff(this.timerStartMoment));
        const hours = Math.floor(duration.asHours()).toString().padStart(2, '0');
        const minutes = duration.minutes().toString().padStart(2, '0');
        const seconds = duration.seconds().toString().padStart(2, '0');

        this.activeTimer = `${hours}:${minutes}:${seconds}`;
    }

    /**
     * calculateHoursAndMinutes prepares the time strings for the template
     */
    private calculateHoursAndMinutes(): void {
        const hoursToday = Math.floor(this.minutesToday / 60);
        const minutesToday = this.minutesToday % 60;

        // create the time strings, with a leading 0 for minutes if needed
        this.timeWorkedToday = `${hoursToday}h ${minutesToday < 10 ? '0' : ''}${minutesToday}m`;
    }

    private async loadLatestActiveEntry(): Promise<void> {
        try {
            const latestActiveEntry = await this.timeClockService.getLatestActiveEntry();

            if (latestActiveEntry.data) {
                this.latestActiveEntry = new TimeClockEntry(latestActiveEntry);
                this.timeClockEntry = this.latestActiveEntry;
                this.selectedProjectId = this.latestActiveEntry.projectId;
                this.isClockedIn = true;

                // Set the hour type based on the latest entry
                if (
                    this.latestActiveEntry.salaryId ||
                    this.latestActiveEntry.incomeTypeId ||
                    this.latestActiveEntry.incomeType
                ) {
                    // Will be set in setHourTypes() after hour types are loaded
                    this.selectedHourType = null;
                } else {
                    // Explicitly mark as unpaid
                    this.selectedHourType = null;
                    this.timeClockEntry.salaryId = null;
                    this.timeClockEntry.incomeType = null;
                    this.timeClockEntry.incomeTypeId = null;
                }

                const startMoment = moment(this.latestActiveEntry.startTime, 'YYYY-MM-DD HH:mm:ss');
                this.dayOfMonth = startMoment.date();
                const diffInMilliseconds = moment().diff(startMoment);
                const timerStartTime = moment().subtract(diffInMilliseconds, 'milliseconds');
                this.startTimer(timerStartTime);
            }
        } catch (error) {
            this.notify.error(error);
        } finally {
            this.isLoading = false;
        }
    }

    private async setHourTypes(): Promise<void> {
        if (!this.auth.employee) {
            this.clearHourTypeData();
            return;
        }

        if (!this.timeTrackingSettings?.payrollIntegration) {
            return;
        }
        this.isLoading = true;
        try {
            const freshHourTypes = await this.employeeHourTypesService.getHourTypes(
                this.auth.employee,
                moment().format('YYYY-MM-DD')
            );

            this.hourTypes = freshHourTypes;
            this.setSelectedHourType();
        } catch (error) {
            this.notify.error(error);
        } finally {
            this.isLoading = false;
        }
    }

    private clearHourTypeData(): void {
        this.hourTypes = [];
        this.selectedHourType = null;
    }

    private setSelectedHourType(): void {
        // If there's no active entry or hour types, don't do anything
        if (!this.latestActiveEntry || !this.hourTypes.length) {
            return;
        }

        // If explicitly set to unpaid (all relevant fields are null), keep it that way
        if (
            this.selectedHourType === null &&
            !this.timeClockEntry.salaryId &&
            !this.timeClockEntry.incomeType &&
            !this.timeClockEntry.incomeTypeId
        ) {
            return;
        }
        // Find the matching hour type based on either salaryId or incomeTypeId
        const matchingHourType = this.hourTypes.find(
            (hourType) =>
                (this.timeClockEntry.salaryId && hourType.salaryId === this.timeClockEntry.salaryId) ||
                (this.timeClockEntry.incomeTypeId && hourType.id === this.timeClockEntry.incomeTypeId)
        );

        // Set the hour type if found, otherwise default to the first one
        this.selectedHourType = matchingHourType || this.hourTypes[0];
    }

    private async checkForOverlappingEntries(): Promise<boolean> {
        const currentMoment = moment().startOf('minute');
        try {
            for (const entry of this.timeClockEntries) {
                if (entry.endTime) {
                    const entryStart = moment(entry.startTime).startOf('minute');
                    const entryEnd = moment(entry.endTime).startOf('minute');

                    if (currentMoment.isSame(entryStart, 'day')) {
                        // Check if the current time overlaps with any existing time entries for today
                        if (currentMoment.isBetween(entryStart, entryEnd, 'minute', '[]')) {
                            this.notify.warn('time-tracking.timeClockWidget.overlappingEntry', {
                                onClick: ['/time-tracking', 'my-timesheet', entry.timeTrackingTimesheetEntryId, 'edit'],
                                text: 'time-tracking.timeClockWidget.viewTimesheet',
                            });

                            this.analyticService.trackEvent(AnalyticEvents.TimeClockOverlappingEntry);
                            return true;
                        }
                    }
                }
            }

            return false;
        } catch (error) {
            this.notify.error(error);
            return true;
        }
    }
}
